import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';
import isEqual from 'react-fast-compare';
import {debounce} from 'ts-debounce';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {GetAnalysisScratchFile} from 'analysis-tool/AnalysisTypes';
import {useAutoUpdatePreview} from 'analysis-tool/data-frame-creation/hooks/useAutoUpdatePreview';
import ChartContent from 'charts/chart-content/ChartContent';
import {Chart} from 'charts/ChartTypes';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import DataFrameTableWrapper from 'data-frames/wrapper/DataFrameTableWrapper';
import PersistedDataFrameTableWrapper from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExternalFilePaneContent from 'pipelines/files/ExternalFilePaneContent';
import {PreviewStepParams} from 'pipelines/network/PipelineDAO';
import {PipelineStep} from 'pipelines/PipelineTypes';
import {getAllOutputStepDataRawDatas} from 'pipelines/StepDataUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {
  getMostRecentData,
  NOT_FETCHED_ASYNC_STATE,
  AsyncStatus,
  AsyncState,
} from 'ui-library/hooks/useAsyncState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

const PREVIEW_CARD_MARGIN = 12;

const previewGlobalState = makeUseGlobalClientState<
  AsyncState<{
    dataFrames: Record<string, Array<DataFrame>>;
    charts: Record<string, Array<Chart>>;
    files: Record<string, Array<GetAnalysisScratchFile>>;
  }>
>(NOT_FETCHED_ASYNC_STATE);

export interface PreviewStepDataListProps {
  draftStep: PipelineStep;
  originalStep: PipelineStep;
  clientStateId: string;
  isComplete: boolean;
  errors: Array<string>;
}

export interface PreviewStepDataListState {
  previewDataFrames: Array<DataFrame>;
  previewCharts: Array<Chart>;
  previewFiles: Array<GetAnalysisScratchFile>;
  previewStatus: AsyncStatus;
  statusMessage: string | null;
}

export function NoDataMessage(): JSX.Element {
  return (
    <div className={css(previewStyles.previewCard)}>
      <InlineBanner bannerType={InlineBannerType.WARNING} text="No preview data available." />
    </div>
  );
}

function StepDataList({
  previewDataFrames,
  previewCharts,
  previewFiles,
  clientStateId,
}: {
  previewDataFrames: Array<DataFrame>;
  previewCharts: Array<Chart>;
  previewFiles: Array<GetAnalysisScratchFile>;
  clientStateId: string;
}): JSX.Element {
  if (!previewDataFrames.length && !previewCharts.length && !previewFiles.length) {
    return <NoDataMessage />;
  }

  const hasMultipleCharts = previewCharts.length > 1;
  const hasMultipleTables = previewDataFrames.length > 1;
  const hasMultipleFiles = previewFiles.length > 1;

  return (
    <div className={css(previewStyles.container)}>
      {previewCharts.map((chart, index) => (
        <div key={index} className={css(previewStyles.previewCard)}>
          <div className={css(previewStyles.title)}>
            {hasMultipleCharts ? `Output chart ${index + 1}` : 'Output chart'}
          </div>
          <div className={css(previewStyles.content)}>
            <ChartContent chart={chart} />
          </div>
        </div>
      ))}
      {previewDataFrames.map((dataFrame, index) => (
        <div key={index} className={css(previewStyles.previewCard)}>
          <div className={css(previewStyles.title)}>
            {hasMultipleTables ? `Output table ${index + 1}` : 'Output table'}
          </div>
          {dataFrame.rows.length === 0 && (
            <div className={css(previewStyles.banner)}>
              <InlineBanner bannerType={InlineBannerType.WARNING} text="This table has no rows." />
            </div>
          )}
          <div className={css(previewStyles.content)}>
            <DataFrameTableWrapper
              actionOrigin={ActionOrigin.ANALYSIS_TOOL}
              clientStateId={`${clientStateId}-${index}`}
              dataFrame={dataFrame}
              defaultTableMode={DataFrameTableMode.TEXT}
              renderIsExcludedColumn={false}
            />
          </div>
        </div>
      ))}
      {previewFiles.map((file, index) => (
        <div key={file.id} className={css(previewStyles.previewCard)}>
          <div className={css(previewStyles.title)}>
            {hasMultipleFiles ? `Output file ${index + 1}` : 'Output file'}
          </div>
          <ExternalFilePaneContent scratchFile={file} />
        </div>
      ))}
    </div>
  );
}

function OriginalStepOutputPreview({step}: {step: PipelineStep}): JSX.Element {
  const dataFrames = useMemo(() => getAllOutputStepDataRawDatas(step, PipelineStepDataKind.DATASET), [step]);
  const charts = useMemo(() => getAllOutputStepDataRawDatas(step, PipelineStepDataKind.CHART), [step]);
  const scratchFiles = useMemo(() => getAllOutputStepDataRawDatas(step, PipelineStepDataKind.FILE), [step]);

  const totalItems = dataFrames.length + charts.length + scratchFiles.length;

  if (totalItems === 0) {
    return (
      <div className={css(previewStyles.previewCard)}>
        <InlineBanner bannerType={InlineBannerType.INFO} text="This step has no outputs." />
      </div>
    );
  }

  const hasMultipleCharts = charts.length > 1;
  const hasMultipleTables = dataFrames.length > 1;
  const hasMultipleFiles = scratchFiles.length > 1;

  return (
    <div className={css(previewStyles.container)}>
      {charts.map((chart, index) => (
        <div key={chart.id} className={css(previewStyles.previewCard)}>
          <div className={css(previewStyles.title)}>
            {hasMultipleCharts ? `Output chart ${index + 1}` : 'Output chart'}
          </div>
          <div className={css(previewStyles.content)}>
            <ChartContent chart={chart} />
          </div>
        </div>
      ))}
      {dataFrames.map((dataFrame, index) => (
        <div key={dataFrame.id} className={css(previewStyles.previewCard)}>
          <div className={css(previewStyles.title)}>
            {hasMultipleTables ? `Output table ${index + 1}` : 'Output table'}
          </div>
          <div className={css(previewStyles.content)}>
            <PersistedDataFrameTableWrapper
              actionOrigin={ActionOrigin.ANALYSIS_TOOL}
              clientStateId={`output-preview-${step.id}-${dataFrame.id}`}
              dataFrameId={dataFrame.id}
            />
          </div>
        </div>
      ))}
      {scratchFiles.map((file, index) => (
        <div key={file.id} className={css(previewStyles.previewCard)}>
          <div className={css(previewStyles.title)}>
            {hasMultipleFiles ? `Output file ${index + 1}` : 'Output file'}
          </div>
          <ExternalFilePaneContent scratchFile={file} />
        </div>
      ))}
    </div>
  );
}

function useLivePreviewData({
  draftStep,
  clientStateId,
}: {
  draftStep: PipelineStep;
  clientStateId: string;
}): PreviewStepDataListState {
  const [previewState, setPreviewState] = previewGlobalState.useGlobalClientState(clientStateId);

  const livePreviewRequestData = draftStep.config
    ? {
        stepType: draftStep.stepType,
        stepConfig: draftStep.config,
        stepInput: Object.fromEntries(
          draftStep.inputStepDatas.map((inputStepData) => [
            inputStepData.name,
            inputStepData.rawDatas.map(({id}) => id),
          ])
        ),
      }
    : null;

  const previewDataFrames = Object.values(getMostRecentData(previewState)?.dataFrames ?? {}).flatMap(
    (dataFrames) => dataFrames
  );
  const previewCharts = Object.values(getMostRecentData(previewState)?.charts ?? {}).flatMap(
    (charts) => charts
  );
  const previewFiles = Object.values(getMostRecentData(previewState)?.files ?? {}).flatMap((files) => files);

  const debouncedGeneratePreview = useMemo(
    () =>
      debounce(
        (
          data: PreviewStepParams<PipelineStepType>,
          setAsyncState: (
            nextState: AsyncState<
              {dataFrames: Record<string, Array<DataFrame>>},
              [PreviewStepParams<PipelineStepType>]
            >
          ) => void
        ) => {
          AnalysisActions.runPreviewAnalysisStep(data, {status: AsyncStatus.NOT_FETCHED}, setAsyncState);
        },
        300
      ),
    []
  );

  useAutoUpdatePreview(
    livePreviewRequestData as PreviewStepParams<PipelineStepType> | null,
    debouncedGeneratePreview,
    setPreviewState
  );

  return {
    previewDataFrames,
    previewCharts,
    previewFiles,
    previewStatus: previewState.status,
    statusMessage: (previewState.status === AsyncStatus.ERROR && previewState.message) || null,
  };
}

function DraftStepDataPreview({
  draftStep,
  clientStateId,
}: {
  draftStep: PipelineStep;
  clientStateId: string;
}): JSX.Element {
  const {previewDataFrames, previewCharts, previewFiles, previewStatus, statusMessage} = useLivePreviewData({
    draftStep,
    clientStateId,
  });

  if (previewStatus === AsyncStatus.LOADING) {
    return <CenteredHorizontalThrobber />;
  }

  if (previewStatus === AsyncStatus.ERROR) {
    if (statusMessage) {
      const cannotPreviewPattern = /^Step type .+ cannot be previewed\.$/;
      if (cannotPreviewPattern.test(statusMessage)) {
        return (
          <div className={css(previewStyles.previewCard)}>
            <InlineBanner
              bannerType={InlineBannerType.WARNING}
              text="This step type cannot be previewed without saving."
            />
          </div>
        );
      }
    }
    return (
      <div className={css(previewStyles.previewCard)}>
        <InlineBanner bannerType={InlineBannerType.ERROR} text="A preview table could not be generated." />
      </div>
    );
  }

  return (
    <StepDataList
      previewDataFrames={previewDataFrames}
      previewCharts={previewCharts}
      previewFiles={previewFiles}
      clientStateId={clientStateId}
    />
  );
}

export default function PreviewStepDataList({
  draftStep,
  originalStep,
  clientStateId,
  isComplete,
  errors,
}: PreviewStepDataListProps): JSX.Element {
  if (!isComplete || errors.length > 0) {
    const message = !isComplete ? 'Step config is incomplete' : 'Step config is invalid';
    return (
      <div className={css(previewStyles.previewCard)}>
        <InlineBanner bannerType={InlineBannerType.WARNING} text={message} />
      </div>
    );
  }

  const shouldShowOriginalData = isEqual(draftStep, originalStep);

  if (shouldShowOriginalData) {
    return <OriginalStepOutputPreview step={originalStep} />;
  }

  return <DraftStepDataPreview draftStep={draftStep} clientStateId={clientStateId} />;
}

const previewStyles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'auto',
    paddingBottom: PREVIEW_CARD_MARGIN / 2,
    paddingTop: PREVIEW_CARD_MARGIN / 2,
  },
  previewCard: {
    borderRadius: 4,
    display: 'flex',
    flexDirection: 'column',
    height: 300,
    marginBottom: PREVIEW_CARD_MARGIN / 2,
    marginLeft: PREVIEW_CARD_MARGIN,
    marginRight: PREVIEW_CARD_MARGIN,
    marginTop: PREVIEW_CARD_MARGIN / 2,
  },
  title: {
    marginBottom: 12,
    fontWeight: 'bold',
    fontSize: 14,
  },
  content: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
  },
  banner: {
    marginBottom: 8,
  },
});
